/**
 * 用户标识（userId）获取
 *
 * - 客户端：getUserId()，从当前页 URL 参数 + document.cookie 取字符串
 * - 服务端：getUserIdStringFromRequest(query, cookie) 取字符串，getUserIdFromRequest(urlParams, headers) 取数字
 *
 * 取值规则（两端一致）：先 query，再 cookie；同一来源内按常量中的 key 顺序取第一个有效值（trim 后非空）。
 *
 * 参考：
 * - https://km.sankuai.com/collabpage/2740975519
 * - https://km.sankuai.com/collabpage/2741968523
 */

import { getFirstCookieClient, getFirstCookieFromHeader } from "./cookie";
import { getCacheUserInfo } from "./login";
import { getUrlParams, getFirstValue } from "./urlParams";
import { getCacheUuid } from "./uuid";

/** Query 中 userId 的多种写法（与 middleware/SSR 一致），按数组顺序优先：userid → userId → user_id */
const USER_ID_QUERY_KEYS = ["userid", "userId", "user_id"] as const;
/** Cookie 中 userId 的多种写法，按数组顺序优先：userId → userid → user_id → u */
// 在cookei中取的目前的场景主要是浏览器，微信浏览器中有出现一个用户两个账号同时有userid\userId的情况，并且userId才是最新账号信息
const USER_ID_COOKIE_KEYS = ["userId", "userid", "user_id", "u"] as const;

// ========== 客户端用 ==========

/**
 * 从当前页 URL 参数和 document.cookie 获取 userId 字符串（仅浏览器环境）。
 * 优先级：getUrlParams() 的 query → getCookie() 的 cookie，各自按 USER_ID_*_KEYS 顺序。
 */
export function getUserId(): string | undefined {
  if (getCacheUserInfo()?.userId) {
    return String(getCacheUserInfo()!.userId);
  }
  return getFirstValue(getUrlParams(), USER_ID_QUERY_KEYS) ?? getFirstCookieClient(USER_ID_COOKIE_KEYS);
}

// ========== 服务端用 ==========

/**
 * 从请求的 query 和 cookie 字符串获取 userId 字符串（用于 SSR、middleware 等）。
 * @param query 请求 query 对象（如 context.query）
 * @param cookie 请求 cookie 字符串（如 context.cookie 或 headers['cookie']）
 * 优先级：query 按 USER_ID_QUERY_KEYS → cookie 按 USER_ID_COOKIE_KEYS
 */
export function getUserIdStringFromRequest(
  query?: Record<string, string | undefined> | null,
  cookie?: string | null,
): string | undefined {
  return (
    getFirstValue(query ?? {}, USER_ID_QUERY_KEYS) ??
    getFirstCookieFromHeader(cookie ?? "", USER_ID_COOKIE_KEYS)
  );
}

/**
 * 从请求的 URLSearchParams 和 headers 中解析 userId 数字（用于 middleware 分流等）。
 * 内部将 urlParams 转为 query、从 headers 取 cookie，再调用 getUserIdStringFromRequest 后转为 number；无效或非数字返回 undefined。
 */
export function getUserIdFromRequest(
  urlParams: URLSearchParams,
  headers: Record<string, string>,
): number | undefined {
  const query = Object.fromEntries(urlParams.entries()) as Record<string, string>;
  const cookie = headers["cookie"] ?? headers["Cookie"] ?? "";
  const str = getUserIdStringFromRequest(query, cookie);
  if (!str) return undefined;
  const n = Number(str);
  return Number.isFinite(n) ? n : undefined;
}

export function getUnionId(): string {
  return getUserId() || getCacheUuid() || "";
}
